/**
 * Componente wrapper que combina Textarea com botão de inserção de variáveis
 * Permite digitação normal, inserção de variáveis via botão {} e formatação de texto
 * Suporta formatação WhatsApp: *negrito*, _itálico_, ~tachado~
 */

import { useRef, forwardRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { VariableInsertButton } from './VariableInsertButton';
import { EmojiInsertButton } from './EmojiInsertButton';
import { Button } from '@/components/ui/button';
import { Bold, Italic, Strikethrough } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TextareaWithVariablesProps {
  tenantId: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  className?: string;
}

/**
 * Envolve o texto selecionado no textarea com marcadores de formatação WhatsApp
 * Se não houver seleção, insere marcadores vazios na posição do cursor
 * 
 * @param textarea - Referência ao elemento textarea
 * @param marker - Caractere marcador (* para negrito, _ para itálico, ~ para tachado)
 * @param onChange - Callback para atualizar o valor
 */
function applyFormatting(
  textarea: HTMLTextAreaElement | null,
  marker: string,
  onChange: (value: string) => void
) {
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const currentValue = textarea.value;
  const selectedText = currentValue.substring(start, end);

  // Envolve o texto selecionado com o marcador
  const formatted = selectedText
    ? `${marker}${selectedText}${marker}`
    : `${marker}${marker}`;

  const newValue = currentValue.substring(0, start) + formatted + currentValue.substring(end);
  onChange(newValue);

  // Reposiciona cursor dentro dos marcadores (se sem seleção) ou após (se com seleção)
  setTimeout(() => {
    textarea.focus();
    if (selectedText) {
      const newPos = start + formatted.length;
      textarea.setSelectionRange(newPos, newPos);
    } else {
      const cursorPos = start + marker.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }
  }, 0);
}

export const TextareaWithVariables = forwardRef<HTMLTextAreaElement, TextareaWithVariablesProps>(
  ({ tenantId, value, onChange, placeholder, maxLength, rows = 4, className }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    return (
      <div className="relative">
        {/* Barra de formatação de texto (negrito, itálico, tachado) */}
        <div className="flex items-center gap-0.5 mb-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Negrito (*texto*)"
            onClick={() => applyFormatting(textareaRef.current, '*', onChange)}
          >
            <Bold className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Itálico (_texto_)"
            onClick={() => applyFormatting(textareaRef.current, '_', onChange)}
          >
            <Italic className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Tachado (~texto~)"
            onClick={() => applyFormatting(textareaRef.current, '~', onChange)}
          >
            <Strikethrough className="h-3.5 w-3.5" />
          </Button>
        </div>

        <Textarea
          ref={(node) => {
            // Sincroniza ref interno e ref externo
            if (textareaRef) {
              (textareaRef as any).current = node;
            }
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          className={cn('pr-20', className)}
        />
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          <EmojiInsertButton
            textareaRef={textareaRef}
            onInsert={onChange}
          />
          <VariableInsertButton
            tenantId={tenantId}
            textareaRef={textareaRef}
            onInsert={onChange}
          />
        </div>
      </div>
    );
  }
);

TextareaWithVariables.displayName = 'TextareaWithVariables';
